import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {ClientEvent, MatrixEvent, type MatrixClient} from 'matrix-js-sdk';
import {getApiUrl} from '../config';
import {store, type RootState} from '../store/store';
import {usersApi} from '../store/features/Users/userService';
import {authenticatedFetch} from '../services/authRuntime';
import {getActiveMatrixCallIdentity} from '../services/matrixCallService';
import {CallStateRepairWorker, isRepairRecord, retainRepairEvidence, type RepairCandidate, type RepairTimelineEvent} from '../services/matrixCalling/CallStateRepair';
import {flushCallAccounting} from '../services/matrixCalling/CallAccountingOutbox';
import logger from '../services/logger';

export function useCallStateRepair(options: {isInitialized: boolean; getClient: () => MatrixClient | null}): void {
    const tenant = useSelector((state: RootState) => state.auth.tenant);
    const userId = useSelector((state: RootState) => state.auth.userId);
    const {isInitialized, getClient} = options;
    useEffect(() => {
        const client = getClient();
        if (!isInitialized || !tenant || !userId || !client) return;
        const cache = new Map<string, RepairTimelineEvent>();
        const worker = new CallStateRepairWorker({
            scope: 'phonehq:repair:' + JSON.stringify([tenant, Number(userId)]), reporter: Number(userId),
            storage: {
                get: key => localStorage.getItem(key) ?? undefined,
                set: (key, value) => localStorage.setItem(key, value),
                remove: key => localStorage.removeItem(key),
                keys: prefix => Object.keys(localStorage).filter(key => key.startsWith(prefix)),
            },
            ready: () => document.visibilityState !== 'hidden' && store.getState().auth.tenant === tenant &&
                store.getState().auth.userId === userId && !!client.getAccessToken(),
            active: candidate => {
                const active = getActiveMatrixCallIdentity();
                return !!active && (active.matrixCallId === candidate.matrixCallId ||
                    active.backendCallId === String(candidate.callId) ||
                    (!candidate.matrixCallId && active.roomId === candidate.roomId));
            },
            candidates: async signal => {
                const response = await authenticatedFetch(
                    (await getApiUrl()) + '/api/v1/call-history/chat-state?userId=' + userId + '&callState=STARTED,TALKING',
                    {headers: {tenant}, signal},
                );
                if (!response.ok) throw new Error('Call state unavailable');
                const body = await response.json();
                if (!Array.isArray(body.data)) throw new Error('Invalid call state');
                return body.data;
            },
            page: async ({candidate, cursor, signal}) => readPage({client, candidate, cursor, signal, cache}),
            resolveParticipant: async matrixId => {
                const response = await store.dispatch(usersApi.endpoints.resolveMatrixIds.initiate(
                    {matrixIds: [matrixId]}, {subscribe: false},
                )).unwrap();
                return Number(response[matrixId]) || undefined;
            },
            submit: async (repair, signal) => {
                const response = await authenticatedFetch(
                    (await getApiUrl()) + '/api/v1/call-history/chat-state/' + repair.callId + '/repair',
                    {method: 'POST', headers: {'Content-Type': 'application/json', tenant,
                        'X-Matrix-Authorization': client.getAccessToken() ?? ''}, body: JSON.stringify(repair), signal},
                );
                if (response.status !== 202) throw new Error('Repair not accepted');
            },
            report: (candidate, reason) => logger.info('Matrix call reconciliation', {
                source: 'matrix_call', event: 'reconciliation', tenant, reporter: userId,
                callId: candidate ? String(candidate.callId) : undefined, matrixCallId: candidate?.matrixCallId, roomId: candidate?.roomId, reason,
            }),
        });
        const run = () => { void flushCallAccounting(); void worker.run(); };
        const onSync = (state: string, previous: string | null) => {
            if (state !== previous && (state === 'PREPARED' || state === 'SYNCING')) run();
        };
        run();
        const interval = window.setInterval(run, 15_000);
        window.addEventListener('online', run);
        window.addEventListener('focus', run);
        document.addEventListener('visibilitychange', run);
        client.on(ClientEvent.Sync, onSync);
        return () => {
            worker.stop();
            window.clearInterval(interval);
            window.removeEventListener('online', run);
            window.removeEventListener('focus', run);
            document.removeEventListener('visibilitychange', run);
            client.removeListener(ClientEvent.Sync, onSync);
        };
    }, [isInitialized, getClient, tenant, userId]);
}

async function readPage(request: {
    client: MatrixClient; candidate: RepairCandidate; cursor?: string; signal: AbortSignal; cache: Map<string, RepairTimelineEvent>;
}) {
    const {client, candidate, cursor, signal} = request;
    if (client.getRoom(candidate.roomId)?.getMyMembership() !== 'join') throw new Error('Room unavailable');
    const response = await fetch(client.getHomeserverUrl() + '/_matrix/client/v3/rooms/' +
        encodeURIComponent(candidate.roomId) + '/messages?dir=b&limit=50' +
        (cursor ? '&from=' + encodeURIComponent(cursor) : ''), {
        headers: {Authorization: 'Bearer ' + client.getAccessToken()}, signal,
    });
    if (!response.ok) throw new Error('History unavailable');
    const page: unknown = await response.json();
    if (!isRepairRecord(page) || !Array.isArray(page.chunk)) throw new Error('Invalid history');
    const events: RepairTimelineEvent[] = [];
    let oldestTimestamp = Infinity;
    for (const raw of page.chunk) {
        if (!isRepairRecord(raw) || typeof raw.event_id !== 'string' || typeof raw.sender !== 'string' ||
            typeof raw.origin_server_ts !== 'number' || typeof raw.type !== 'string' || !isRepairRecord(raw.content)) {
            throw new Error('Invalid event');
        }
        oldestTimestamp = Math.min(oldestTimestamp, raw.origin_server_ts);
        const cacheKey = candidate.roomId + ':' + raw.event_id;
        const cached = request.cache.get(cacheKey);
        if (cached) { events.push(cached); continue; }
        const event = new MatrixEvent({event_id: raw.event_id, sender: raw.sender, origin_server_ts: raw.origin_server_ts,
            room_id: candidate.roomId, type: raw.type, content: raw.content});
        await client.decryptEventIfNeeded(event);
        if (event.getType() === 'm.room.encrypted' || event.isDecryptionFailure()) throw new Error('Keys unavailable');
        const evidence = retainRepairEvidence({eventId: raw.event_id, sender: raw.sender, timestamp: raw.origin_server_ts,
            type: event.getType(), content: event.getContent()});
        events.push(evidence);
        request.cache.set(cacheKey, evidence);
        if (request.cache.size > 1_000) request.cache.delete(request.cache.keys().next().value ?? '');
    }
    return {events, next: typeof page.end === 'string' ? page.end : undefined, oldestTimestamp};
}
